import { useEffect, useRef, useState } from "react";
import { CommunityGroups } from "../components/education/CommunityGroups";
import { RecommendedContent } from "../components/education/RecommendedContent";
import { RequiredTraining } from "../components/education/RequiredTraining";
import { ResumeCarousel } from "../components/education/ResumeCarousel";
import { Header } from "../components/Header";
import { useUserType } from "../hooks/useUserType";
import { useAnalytics } from "../lib/analytics";
import { filterByLocale } from "../lib/edhub/localeFilter";
import { useClientTranslations } from "../lib/i18n/client";
import {
	type EducationContent,
	fetchPodcastProgress,
	fetchRecommendedContent,
	MOCK_COMMUNITY_GROUPS,
	MOCK_TRAINING,
	RECOMMENDED_FALLBACK,
	type ResumeItem,
} from "../services/educationService";
import styles from "./EducationHub.module.css";

export function EducationHub() {
	const { logEducation } = useAnalytics();
	const { t, locale } = useClientTranslations("education");
	const { isD2C } = useUserType();

	// Recommended content comes from era-api `/api/education-hub/articles`
	// filtered by the patient's `clinic_id` JWT claim. We fall back to the
	// mock list if the call fails or returns nothing. Training / community are
	// still mock-backed; Browse library was removed pending a from-scratch
	// rebuild with content-prism categories.
	// null = not yet loaded. Show nothing until the real fetch resolves, then
	// use real data when present and fall back to mock only when there's none
	// — avoids the flash where mock renders first and is then replaced.
	const [recommended, setRecommended] = useState<EducationContent[] | null>(
		null,
	);

	// Resume: real server-persisted podcast progress (era-api PR #153). null =
	// not yet loaded; empty array hides the section.
	const [resume, setResume] = useState<ResumeItem[] | null>(null);

	useEffect(() => {
		let cancelled = false;
		fetchRecommendedContent()
			.then((items) => {
				if (!cancelled)
					setRecommended(items.length > 0 ? items : RECOMMENDED_FALLBACK);
			})
			.catch((err) => {
				console.warn(
					"[EducationHub] recommended content fetch failed; using fallback:",
					err,
				);
				if (!cancelled) setRecommended(RECOMMENDED_FALLBACK);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		let cancelled = false;
		fetchPodcastProgress()
			.then((items) => {
				if (!cancelled) setResume(items);
			})
			.catch((err) => {
				console.warn("[EducationHub] resume fetch failed:", err);
				if (!cancelled) setResume([]);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	// Resume items point at the language-specific content rows (Hebrew twins
	// have no podcasts), so apply the same locale split as the hub lists.
	const resumeForLocale = resume ? filterByLocale(resume, locale) : null;

	const hasTrackedPageView = useRef(false);
	useEffect(() => {
		if (!hasTrackedPageView.current) {
			hasTrackedPageView.current = true;
			logEducation("hub_page_viewed");
		}
	}, [logEducation]);

	return (
		<div className={styles.wrapper}>
			<Header />
			<main className={styles.main}>
				{/* Page title */}
				<div className={styles.pageHeader}>
					<h1 className={styles.pageTitle}>{t("page.title")}</h1>
					<p className={styles.pageSubtitle}>{t("page.subtitle")}</p>
				</div>

				{/* Recommended now section */}
				<section className={styles.section}>
					<div className={styles.sectionHeader}>
						<h2 className={styles.sectionHeading}>
							{t("recommended.heading")}
						</h2>
						<p className={styles.sectionSubtitle}>
							{t("recommended.subtitle")}
						</p>
					</div>
					{recommended && (
						<RecommendedContent items={filterByLocale(recommended, locale)} />
					)}
				</section>

				{/* Required training section */}
				{!isD2C && <RequiredTraining items={MOCK_TRAINING} />}

				{/* Continue where you left off — real podcast progress; hidden when empty */}
				{resumeForLocale && resumeForLocale.length > 0 && (
					<section className={styles.section}>
						<div className={styles.sectionHeader}>
							<h2 className={styles.sectionHeading}>{t("resume.heading")}</h2>
						</div>
						<ResumeCarousel items={resumeForLocale} />
					</section>
				)}

				{/* Community groups section */}
				{!isD2C && (
					<section className={styles.section}>
						<div className={styles.sectionHeader}>
							<h2 className={styles.sectionHeading}>{t("groups.heading")}</h2>
						</div>
						<CommunityGroups items={MOCK_COMMUNITY_GROUPS} />
					</section>
				)}
			</main>
		</div>
	);
}
